import {
    Add24Regular,
    CalendarClock24Regular,
    Clock24Regular,
    History24Regular
} from "@fluentui/react-icons";
import {FC} from "react";
import IntelligenceGlyph from "./IntelligenceGlyph";
import {Agent, AgentStatus} from "./intelligenceModels";

interface IntelligenceAgentsViewProps {
    agents: Agent[];
    onToggle: (id: string) => void;
}

const STATUS_LABEL: Record<AgentStatus, string> = {
    running: "Running",
    scheduled: "Scheduled",
    paused: "Paused"
};

const IntelligenceAgentsView: FC<IntelligenceAgentsViewProps> = ({
    agents,
    onToggle
}) => (
    <div className="pi-scroll">
        <div className="pi-view-head">
            <h2 className="pi-view-head__title">Agents</h2>
            <p className="pi-view-head__desc">
                Autonomous workers that monitor your business and act on a
                schedule.
            </p>
        </div>

        {agents.map((agent) => (
            <div className="pi-agent-card" key={agent.id}>
                <div className="pi-agent-card__head">
                    <span className="pi-agent-card__icon">
                        <IntelligenceGlyph icon={agent.icon} />
                    </span>
                    <div className="pi-agent-card__body">
                        <div className="pi-agent-card__name">{agent.name}</div>
                        <div className="pi-agent-card__desc">
                            {agent.description}
                        </div>
                    </div>
                    <div className="pi-agent-card__top-right">
                        <span className={`pi-status pi-status--${agent.status}`}>
                            <span className="pi-status__dot" />
                            {STATUS_LABEL[agent.status]}
                        </span>
                        <button
                            type="button"
                            className={`pi-toggle${
                                agent.enabled ? " pi-toggle--on" : ""
                            }`}
                            role="switch"
                            aria-checked={agent.enabled}
                            aria-label={`${
                                agent.enabled ? "Disable" : "Enable"
                            } ${agent.name}`}
                            onClick={() => onToggle(agent.id)}
                        />
                    </div>
                </div>

                <div className="pi-agent-card__meta">
                    {agent.schedule && (
                        <div className="pi-agent-card__meta-item">
                            <span className="pi-agent-card__meta-label">
                                Schedule
                            </span>
                            <span className="pi-agent-card__meta-value">
                                <CalendarClock24Regular />
                                {agent.schedule}
                            </span>
                        </div>
                    )}
                    <div className="pi-agent-card__meta-item">
                        <span className="pi-agent-card__meta-label">Last run</span>
                        <span className="pi-agent-card__meta-value">
                            <History24Regular />
                            {agent.lastRun}
                        </span>
                    </div>
                    {agent.nextRun && (
                        <div className="pi-agent-card__meta-item">
                            <span className="pi-agent-card__meta-label">
                                Next run
                            </span>
                            <span className="pi-agent-card__meta-value">
                                <Clock24Regular />
                                {agent.nextRun}
                            </span>
                        </div>
                    )}
                    <div className="pi-agent-card__meta-item">
                        <span className="pi-agent-card__meta-label">
                            Total runs
                        </span>
                        <span className="pi-agent-card__meta-value">
                            {agent.runs.toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>
        ))}

        <button type="button" className="pi-agents__cta">
            <Add24Regular />
            Create agent
        </button>
    </div>
);

export default IntelligenceAgentsView;
